<script lang="ts">
  import * as _ from "lodash";

  // services
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  // components
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import ListsBanner from "@retailer/svelte/OrderGroups/ListsBanner.svelte";
  import OrderGroupComponent from "@retailer/svelte/OrderGroups/OrderGroup.svelte";
  import OrderGroupFilters from "@retailer/svelte/OrderGroups/Filters.svelte";
  import PageWrapper from "@retailer/svelte/Shared/PageWrapper.svelte";
  import PaginationLarge from "@shared/svelte/Components/Pagination/Large.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";

  // types
  import type { Distributor } from "@shared/types/Distributor";
  import type { OrderGroup } from "@shared/types/OrderGroup";
  import type { QuickOrderList } from "@shared/types/QuickOrderList";

  import PunchoutSessionStore from "@retailer/svelte/stores/PunchoutSessionStore";

  export let distributors: Distributor[];
  export let orderGroupsRes: { [key: string]: any };
  export let previouslyOrderedList: QuickOrderList;
  export let recentlyOrderedListsRes;
  export let refreshState;
  export let stateParams: any;

  const { trackClick } = EventTracking;

  const hasRecentlyOrderedList: boolean = !!recentlyOrderedListsRes?.length;
  const perPage = 10;
  const hasPreviouslyOrderedAutogeneratedListsRollout = stagedRolloutCheck(
    "marketplace__previously_ordered_autogenerated_lists",
  );

  const listId: number = hasPreviouslyOrderedAutogeneratedListsRollout
    ? previouslyOrderedList?.id
    : hasRecentlyOrderedList
    ? recentlyOrderedListsRes[0].id
    : null;

  const orderStates = [
    { name: "Confirmed", state: "accepted", id: "accepted" },
    { name: "Seen or Sent", state: "pending", id: "pending" },
    { name: "Rejected", state: "rejected", id: "rejected" },
  ];

  let orderGroups: OrderGroup[] = orderGroupsRes.data;
  let page: number = Number(stateParams.page || 1);

  let pagination = {
    perPage,
    page,
    totalEntries: orderGroupsRes.headers("X-Pagination-Entries"),
    goToPage: (n: Number) => {
      refreshState({ page: n });
    },
  };

  if (stateParams.distributors && !Array.isArray(stateParams.distributors)) {
    stateParams.distributors = [stateParams.distributors];
  }

  if (stateParams.orderStates && !Array.isArray(stateParams.orderStates)) {
    stateParams.orderStates = [stateParams.orderStates];
  }

  $: showReorderPanel =
    !window.sessionStorage.getItem("hide-reorder-panel") && hasRecentlyOrderedList;
</script>

<FizzStyles>
  <PageWrapper let:mobileMode useBackgroundWhite={false}>
    <div class="background-white">
      <div class="breadcrumbs fizz-container">
        <div class="grid-container">
          <div class="grid-row padding--smaller">
            <span class="default-subheader--bold">
              {#if hasPreviouslyOrderedAutogeneratedListsRollout}
                Order History
              {:else}
                Your Orders
              {/if}
            </span>
            <span class="default-body--regular align-right float-right">
              <a
                class="underline"
                href="https://support.provi.com/s/article/how-do-i-submit-an-order"
                rel="noopener noreferrer"
                target="_blank"
              >
                Learn more
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="order-index fizz-container fizz-inset-vertical-24">
      <div class="grid-row align-center">
        <div class="grid-col c12-12 c10-12--md c8-12--lg align-left">
          {#if !hasPreviouslyOrderedAutogeneratedListsRollout && showReorderPanel}
            <div
              class="fizz-full-width fizz-radius-medium fizz-flex-row-4 fizz-inset-vertical-12 fizz-stack-24 fizz-surface-teal fizz-relative"
            >
              <img
                class="fizz-align-self-start"
                style="width: 3.5rem; margin-left: 20px;"
                alt="reorder-folder"
                src={window["C"].IMAGE_PATHS["reorder-folder.png"]}
              />
              <div
                class="fizz-full-width fizz-inset-horizontal-24 fizz-inline-16 fizz-flex-row-responsive"
              >
                <div class="fizz-flex-item">
                  <h4 class="fizz-heading-4 fizz-stack-4">Easily order again with lists</h4>
                  <h5 class="fizz-text-small">
                    Save time, and reorder from a list of products you've ordered in the past
                  </h5>
                </div>
                <UIRouterLink
                  stateName="layout.list_details"
                  params={{ listId }}
                  classes={"fizz-text-inherit fizz-underline-none"}
                >
                  <button
                    class="fizz-button fizz-button-small fizz-stack-4 fizz-flex-item"
                    on:click={() => trackClick("list_go_to_lists_from_order_history_banner", [])}
                  >
                    Go to List
                  </button>
                </UIRouterLink>
              </div>
              <Icon
                type="Close"
                extraClassNames="fizz-absolute"
                extraStyles="padding: 1px; cursor: pointer; top: 0.5rem; right: 0.5rem;"
                onPress={() => {
                  showReorderPanel = false;
                  window.sessionStorage.setItem("hide-reorder-panel", "true");
                }}
              />
            </div>
          {/if}
          {#if $PunchoutSessionStore}
            <div class="fizz-radius-medium fizz-surface-teal fizz-inset-squish-16 fizz-stack-12">
              <p>
                Only orders that have been approved in {$PunchoutSessionStore.procurement_system_name}
                appear in Order History. To view pending orders,
                <a
                  class="fizz-text-strong"
                  href={$PunchoutSessionStore.link}
                  rel="noopener noreferrer"
                  target="_blank">go to {$PunchoutSessionStore.procurement_system_name}</a
                >.
              </p>
            </div>
          {/if}
          <OrderGroupFilters {distributors} {stateParams} {refreshState} {orderStates} />
          {#if listId && hasPreviouslyOrderedAutogeneratedListsRollout}
            <ListsBanner {listId} />
          {/if}
          {#each orderGroups as orderGroup}
            <OrderGroupComponent {orderGroup} {mobileMode} />
          {/each}
          {#if !orderGroups.length}
            <div class="grid-col c12-12">
              <div class="margin-top--2 margin-bottom--2 align-center">
                <div class="h2 margin-bottom--default">No orders found</div>
              </div>
            </div>
          {/if}
        </div>
      </div>
      {#if orderGroups.length}
        <div class="grid-row fizz-inset-vertical-16 fizz-styles">
          <div class="grid-col c12-12 align-center">
            <PaginationLarge {pagination} firstSixOnly={true} />
          </div>
        </div>
      {/if}
    </div>
  </PageWrapper>
</FizzStyles>
